import { NavLink, Routes, Route, Link } from "react-router-dom";
import { Home, Sparkles, BookOpen, GraduationCap, Users, MessageCircle, Tag, LogOut, Trophy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CeremonyRSVP from "@/pages/CeremonyRSVP";
import { Ceremonies } from "@/components/members/Ceremonies";
import { Courses } from "@/components/members/Courses";
import { Feed } from "@/components/members/Feed";
import { Messages } from "@/components/members/Messages";
import { Discounts } from "@/components/members/Discounts";
import { MembersBlog } from "@/components/members/MembersBlog";
import { CeremonyCountdown } from "@/components/members/CeremonyCountdown";
import { Accountability, AccountabilityCard } from "@/components/members/Accountability";

const TABS = [
  ["", "Welcome", Home],
  ["ceremonies", "Ceremonies", Sparkles],
  ["accountability", "Weekly Accountability", Trophy],
  ["blog", "Ambassadors Blog", BookOpen],
  ["courses", "Classes & Courses", GraduationCap],
  ["feed", "Community Feed", Users],
  ["messages", "Messages", MessageCircle],
  ["discounts", "Discounts", Tag],
];

const Welcome = () => {
  const { user } = useAuth();
  return (
    <div data-testid="members-welcome">
      <span className="eyebrow">The Healing Ambassadors</span>
      <h1 className="font-serif-h font-light text-4xl sm:text-5xl text-[#577731] mt-4">Welcome home, {user.name.split(" ")[0]}.</h1>
      <p className="mt-6 text-lg text-[#232b1b]/75 max-w-2xl">You were never meant to heal alone, and you were never meant to carry the medicine alone.</p>
      <CeremonyCountdown />
      <AccountabilityCard />
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 mt-12">
        {TABS.slice(1).map(([to, label, Icon]) => (
          <Link key={to} to={`/members/${to}`} data-testid={`welcome-card-${to}`} className="card-soft p-7 hover:border-[#b6689a] hover:-translate-y-1 block">
            <Icon className="text-[#b6689a]" strokeWidth={1.5} />
            <p className="font-serif-h text-2xl mt-4 text-[#232b1b]">{label}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default function Members() {
  const { user, logout } = useAuth();
  return (
    <div data-testid="members-page" className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
      <aside className="lg:col-span-3">
        <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:sticky lg:top-28 pb-2">
          {TABS.map(([to, label, Icon]) => (
            <NavLink key={to} to={`/members${to ? "/" + to : ""}`} end={!to} data-testid={`members-tab-${to || "welcome"}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-full px-5 py-3 text-sm whitespace-nowrap transition-colors duration-300 ${isActive ? "bg-[#577731] text-white" : "hover:bg-[#b1cc90]/30 text-[#232b1b]/80"}`}>
              <Icon size={16} /> {label}
            </NavLink>
          ))}
          {user.role === "admin" && <NavLink to="/admin" data-testid="members-tab-admin" className="rounded-full px-5 py-3 text-sm text-[#b6689a] hover:bg-[#b6689a]/10">Admin Dashboard</NavLink>}
          <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-3 rounded-full px-5 py-3 text-sm text-[#232b1b]/60 hover:text-[#9c3d5c] whitespace-nowrap">
            <LogOut size={16} /> Log out
          </button>
        </nav>
      </aside>
      <main className="lg:col-span-9 min-h-[60vh]">
        <Routes>
          <Route index element={<Welcome />} />
          <Route path="ceremonies" element={<Ceremonies />} />
          <Route path="accountability" element={<Accountability />} />
          <Route path="rsvp/:ceremony" element={<CeremonyRSVP />} />
          <Route path="blog" element={<MembersBlog />} />
          <Route path="courses" element={<Courses />} />
          <Route path="feed" element={<Feed />} />
          <Route path="messages" element={<Messages />} />
          <Route path="discounts" element={<Discounts />} />
        </Routes>
      </main>
    </div>
  );
}
